import { useState, useEffect, useRef, useCallback, useMemo, useId } from 'react';
import { Plus, Loader2, Trash2, RefreshCw, AlertCircle, ChevronDown } from 'lucide-react';
import './IntegrationsPage.css';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import ConfirmationModal from './components/ConfirmationModal';
import SlackAgentIntegrationSection, { SlackMark } from './SlackAgentIntegrationSection';
import {
  getSocialAccounts,
  syncSocialAccounts,
  createSocialConnectUrl,
  removeSocialAccount,
  beginAdIntegration,
  getAdConnectAttempt,
  listAdAccounts,
  listAdIntegrations,
  removeAdIntegration,
  syncAdIntegration,
  configureTikTokAdIdentity,
  getAdPolicy,
  updateAdPolicy,
  getMyOrg,
} from './lib/api';

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 3 * 60 * 1000;
export const adPopupName = (platform) => `ads-connect-${platform}`;

/* Brand marks — lucide has no TikTok logo, so both are inline SVGs for
   visual consistency. */
function TikTokIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );
}

function InstagramIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

function FacebookIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12z" />
    </svg>
  );
}

function YouTubeIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.51 3.5 12 3.5 12 3.5s-7.51 0-9.38.55A3.02 3.02 0 0 0 .5 6.19 31.6 31.6 0 0 0 0 12a31.6 31.6 0 0 0 .5 5.81 3.02 3.02 0 0 0 2.12 2.14c1.87.55 9.38.55 9.38.55s7.51 0 9.38-.55a3.02 3.02 0 0 0 2.12-2.14A31.6 31.6 0 0 0 24 12a31.6 31.6 0 0 0-.5-5.81zM9.6 15.6V8.4l6.27 3.6z" />
    </svg>
  );
}

function XIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.66l-5.22-6.83-5.97 6.83H1.66l7.73-8.84L1.25 2.25h6.83l4.72 6.24zm-1.16 17.52h1.83L6.98 4.13H5.02z" />
    </svg>
  );
}

const PROVIDERS = [
  {
    key: 'tiktok',
    name: 'TikTok',
    description: 'Connect TikTok for publishing and account access.',
    Icon: TikTokIcon,
  },
  {
    key: 'instagram',
    name: 'Instagram',
    description: 'Publish Reels to Instagram. Requires a Business or Creator account.',
    Icon: InstagramIcon,
  },
  {
    key: 'facebook',
    name: 'Facebook',
    description: 'Publish video posts and Reels to a Facebook Page.',
    Icon: FacebookIcon,
  },
  {
    key: 'youtube',
    name: 'YouTube',
    description: 'Upload videos and Shorts to a YouTube channel.',
    Icon: YouTubeIcon,
  },
  {
    key: 'x',
    name: 'X',
    description: 'Post videos and text to X (formerly Twitter).',
    Icon: XIcon,
  },
];

const AD_PROVIDERS = [
  {
    key: 'meta',
    name: 'Meta Ads',
    description: 'Read campaign performance and create paused ads for Facebook and Instagram.',
    Icon: FacebookIcon,
  },
  {
    key: 'tiktok',
    name: 'TikTok Ads',
    description: 'Read TikTok campaign performance and create paused video ads.',
    Icon: TikTokIcon,
  },
];

function accountInitial(account) {
  const source = account.username || account.platform || '?';
  return source.charAt(0).toUpperCase();
}

function AccountRow({ account, onRemove, isRemoving }) {
  return (
    <div className="integrations-account-row">
      {account.profile_photo_url ? (
        <img
          className="integrations-account-avatar"
          src={account.profile_photo_url}
          alt=""
          referrerPolicy="no-referrer"
        />
      ) : (
        <span className="integrations-account-avatar integrations-account-avatar--fallback">
          {accountInitial(account)}
        </span>
      )}
      <div className="integrations-account-info">
        <span className="integrations-account-username">
          {account.username ? `@${account.username}` : 'Connected account'}
        </span>
        <span
          className={`integrations-account-status ${
            account.status === 'connected'
              ? 'integrations-account-status--connected'
              : 'integrations-account-status--disconnected'
          }`}
        >
          {account.status === 'connected' ? 'Connected' : 'Disconnected'}
        </span>
      </div>
      <button
        type="button"
        className="integrations-account-remove"
        onClick={onRemove}
        disabled={isRemoving}
        aria-label={`Remove ${account.username || 'account'}`}
      >
        {isRemoving ? <Loader2 size={15} className="integrations-spin" /> : <Trash2 size={15} />}
      </button>
    </div>
  );
}

export function ProviderCard({ provider, accounts, onConnect, connecting, onRemove, removingId }) {
  const { Icon } = provider;
  const [expanded, setExpanded] = useState(false);
  const accountListId = useId();
  const connectedAccountCount = accounts.filter((account) => account.status === 'connected').length;
  const hasAccounts = accounts.length > 0;

  return (
    <section className="integrations-card">
      <div className="integrations-card-header">
        <button
          type="button"
          className="integrations-card-summary"
          onClick={() => setExpanded((current) => !current)}
          disabled={!hasAccounts}
          aria-expanded={hasAccounts ? expanded : undefined}
          aria-controls={hasAccounts ? accountListId : undefined}
          aria-label={hasAccounts ? `${expanded ? 'Hide' : 'Show'} ${accounts.length} ${provider.name} account${accounts.length === 1 ? '' : 's'}` : undefined}
        >
          <span className={`integrations-card-logo integrations-card-logo--${provider.key}`}>
            <Icon size={22} />
            {connectedAccountCount > 0 && (
              <span className="integrations-card-logo-status" aria-label={`${connectedAccountCount} connected account${connectedAccountCount === 1 ? '' : 's'}`}>
                {connectedAccountCount}
              </span>
            )}
          </span>
          <span className="integrations-card-heading">
            <span className="integrations-card-title">{provider.name}</span>
            <span className="integrations-card-desc">{provider.description}</span>
          </span>
          {hasAccounts && <ChevronDown size={16} className={`integrations-card-chevron${expanded ? ' is-expanded' : ''}`} />}
        </button>
        {!hasAccounts && (
          <button
            type="button"
            className="integrations-card-connect"
            onClick={() => onConnect(provider.key)}
            disabled={connecting}
            aria-label={`Connect ${provider.name} account`}
            title={`Connect ${provider.name} account`}
          >
            {connecting ? <Loader2 size={14} className="integrations-spin" /> : <Plus size={14} />}
          </button>
        )}
      </div>

      {hasAccounts && expanded && (
        <div className="integrations-card-dropdown integrations-social-dropdown" id={accountListId}>
          <div className="integrations-social-actions">
            <span>Connected accounts</span>
            <button
              type="button"
              onClick={() => onConnect(provider.key)}
              disabled={connecting}
              aria-label={`Add another ${provider.name} account`}
            >
              {connecting ? <Loader2 size={13} className="integrations-spin" /> : <Plus size={13} />}
              Add account
            </button>
          </div>
          <div className="integrations-account-list">
            {accounts.map((account) => (
              <AccountRow
                key={account.id}
                account={account}
                onRemove={() => onRemove(account)}
                isRemoving={removingId === account.id}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export function AdProviderCard({ provider, connection, accounts, onConnect, connecting, onSync, syncing, onRemove, onError, canManage }) {
  const { Icon } = provider;
  const active = connection?.status === 'active';
  const [expanded, setExpanded] = useState(false);
  const accountListId = useId();
  const [identityName, setIdentityName] = useState('');
  const [identityImage, setIdentityImage] = useState('');
  const [identityBusy, setIdentityBusy] = useState(false);
  const saveIdentity = async () => {
    if (!connection || !identityName.trim() || !identityImage.trim()) return;
    setIdentityBusy(true);
    try {
      await configureTikTokAdIdentity(connection.id, { display_name: identityName.trim(), image_url: identityImage.trim() });
      setIdentityName(''); setIdentityImage('');
    } catch (err) {
      onError('Could not save the TikTok ads identity.');
    } finally { setIdentityBusy(false); }
  };
  return (
    <section className="integrations-card integrations-card--ads">
      <div className="integrations-card-header">
        <button
          type="button"
          className="integrations-card-summary"
          onClick={() => setExpanded((current) => !current)}
          disabled={!connection}
          aria-expanded={connection ? expanded : undefined}
          aria-controls={connection ? accountListId : undefined}
          aria-label={connection ? `${expanded ? 'Hide' : 'Show'} ${provider.name} connection details` : undefined}
        >
          <span className={`integrations-card-logo integrations-card-logo--${provider.key}`}>
            <Icon size={22} />
            {accounts.length > 0 && (
              <span className="integrations-card-logo-status" aria-label={`${accounts.length} connected ad account${accounts.length === 1 ? '' : 's'}`}>
                {accounts.length}
              </span>
            )}
          </span>
          <span className="integrations-card-heading">
            <span className="integrations-card-title">{provider.name}</span>
            <span className="integrations-card-desc">{provider.description}</span>
          </span>
          {connection && <ChevronDown size={16} className={`integrations-card-chevron${expanded ? ' is-expanded' : ''}`} />}
        </button>
        {!connection && (
          <button
            type="button"
            className="integrations-card-connect"
            onClick={() => onConnect(provider.key)}
            disabled={connecting || !canManage}
            aria-label={`Connect ${provider.name}`}
            title={!canManage ? 'Only an organization owner can manage ad connections.' : `Connect ${provider.name}`}
          >
            {connecting ? <Loader2 size={14} className="integrations-spin" /> : <Plus size={14} />}
          </button>
        )}
      </div>
      {connection && expanded && <div className="integrations-card-dropdown" id={accountListId}>
        <div className="integrations-ad-status">
          <span className={`integrations-account-status ${active ? 'integrations-account-status--connected' : 'integrations-account-status--disconnected'}`}>{active ? 'Connected' : connection.status}</span>
          <div>
            {active && <button type="button" onClick={() => onSync(connection)} disabled={syncing || !canManage}>{syncing ? <Loader2 size={13} className="integrations-spin" /> : <RefreshCw size={13} />} Sync accounts</button>}
            <button type="button" className="is-danger" disabled={!canManage} onClick={() => onRemove(connection)}><Trash2 size={13} /> Disconnect</button>
          </div>
        </div>
        {accounts.length > 0 && <div className="integrations-account-list">
          {accounts.map((account) => <div className="integrations-account-row" key={account.id}>
            <span className="integrations-account-avatar integrations-account-avatar--fallback">{account.name?.charAt(0)?.toUpperCase() || 'A'}</span>
            <div className="integrations-account-info"><span className="integrations-account-username">{account.name}</span><span className="integrations-account-status">{account.currency} · {account.nativeAccountId}</span></div>
            {!account.selectable && <AlertCircle size={15} title={account.unusableReason || 'Unavailable'} />}
          </div>)}
        </div>}
        {active && accounts.length === 0 && <p className="integrations-ad-empty">No selectable ad accounts were returned. Sync after granting account access.</p>}
        {active && provider.key === 'tiktok' && canManage && <details className="integrations-ad-identity"><summary>Ads-only brand identity</summary><label><span>Display name</span><input value={identityName} onChange={(event) => setIdentityName(event.target.value)} /></label><label><span>HTTPS profile image</span><input type="url" value={identityImage} onChange={(event) => setIdentityImage(event.target.value)} /></label><button type="button" className="integrations-card-connect" disabled={identityBusy || !identityName.trim() || !identityImage.trim()} onClick={saveIdentity}>{identityBusy ? <Loader2 size={13} className="integrations-spin" /> : null} Save identity</button></details>}
      </div>}
    </section>
  );
}

function IntegrationsPage() {
  const [slackConnected, setSlackConnected] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [pendingPlatform, setPendingPlatform] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [accountToRemove, setAccountToRemove] = useState(null);
  const [adConnections, setAdConnections] = useState([]);
  const [adAccounts, setAdAccounts] = useState({});
  const [adLoading, setAdLoading] = useState(true);
  const [adConnecting, setAdConnecting] = useState(null);
  const [adPending, setAdPending] = useState(null);
  const [adPolicy, setAdPolicy] = useState(null);
  const [adPolicyDraft, setAdPolicyDraft] = useState(null);
  const [adPolicyBusy, setAdPolicyBusy] = useState(false);
  const [adSyncing, setAdSyncing] = useState(null);
  const [adToRemove, setAdToRemove] = useState(null);
  const [canManageAds, setCanManageAds] = useState(true);
  const [adsAvailable, setAdsAvailable] = useState(true);

  const pollRef = useRef(null);
  const popupRef = useRef(null);
  const accountCountRef = useRef(0);
  const adPollRef = useRef(null);
  const adPopupRef = useRef(null);

  // Local list (our DB) — fast, works even if the provider is down.
  // Sync (provider reconcile) — used when a new account may have appeared.
  const loadAccounts = useCallback(async ({ silent = false, sync = false } = {}) => {
    if (!silent) setIsLoading(true);
    try {
      const result = sync ? await syncSocialAccounts() : await getSocialAccounts();
      const list = result.accounts || [];
      setAccounts(list);
      return list;
    } catch (err) {
      if (!silent) setError('Failed to load connected accounts');
      return null;
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  const loadAdConnections = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setAdLoading(true);
    try {
      const result = await listAdIntegrations();
      const connections = result.connections || [];
      setAdConnections(connections);
      const accountEntries = await Promise.all(connections.filter((item) => item.status === 'active').map(async (item) => {
        try { const response = await listAdAccounts(item.id); return [item.id, response.accounts || []]; }
        catch { return [item.id, []]; }
      }));
      setAdAccounts(Object.fromEntries(accountEntries));
      setAdsAvailable(true);
      if (!silent) setError(null);
      return connections;
    } catch (err) {
      if (['ADS_PILOT_NOT_ENABLED', 'ADS_INTEGRATIONS_DISABLED'].includes(err.code)) {
        setAdsAvailable(false);
      } else if (!silent) {
        setError('Failed to load ad integrations');
      }
      return [];
    } finally {
      if (!silent) setAdLoading(false);
    }
  }, []);

  const loadAdPolicy = useCallback(async () => {
    try {
      const response = await getAdPolicy();
      setAdPolicy(response.policy);
      setAdPolicyDraft({
        allowUnattendedActivation: Boolean(response.policy?.allowUnattendedActivation),
        currencyLimits: Object.fromEntries(Object.entries(response.policy?.currencyLimits || {}).map(([currency, value]) => [currency, typeof value === 'number' ? { dailyBudgetMinor: value, lifetimeBudgetMinor: value, maxPlannedSpendMinor: value } : value])),
      });
    } catch {
      setAdPolicy(null);
      setAdPolicyDraft(null);
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    popupRef.current = null;
    setPendingPlatform(null);
  }, []);

  useEffect(() => {
    // Instant render from our DB, then a silent provider reconcile in the
    // background to catch anything connected outside this page.
    loadAccounts().then(() => loadAccounts({ silent: true, sync: true }));
    loadAdConnections();
    loadAdPolicy();
    getMyOrg().then((response) => setCanManageAds(!response?.organization || response.organization.role === 'owner')).catch(() => setCanManageAds(true));
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (adPollRef.current) clearTimeout(adPollRef.current);
      if (adPopupRef.current && !adPopupRef.current.closed) adPopupRef.current.close();
    };
  }, [loadAccounts, loadAdConnections, loadAdPolicy]);

  // Reconcile when the user returns to this tab (e.g. after finishing OAuth
  // in another tab/popup).
  useEffect(() => {
    const onFocus = () => loadAccounts({ silent: true, sync: true });
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadAccounts]);

  const startPolling = useCallback((platform) => {
    if (pollRef.current) clearInterval(pollRef.current);
    setPendingPlatform(platform);
    const startedAt = Date.now();

    pollRef.current = setInterval(async () => {
      const list = await loadAccounts({ silent: true, sync: true });
      const popupClosed = popupRef.current && popupRef.current.closed;
      const foundNew = list && list.length > accountCountRef.current;
      const timedOut = Date.now() - startedAt > POLL_MAX_MS;

      if (foundNew || timedOut || (popupClosed && list !== null)) {
        stopPolling();
      }
    }, POLL_INTERVAL_MS);
  }, [loadAccounts, stopPolling]);

  const handleConnect = useCallback(async (platform) => {
    setConnectingPlatform(platform);
    setError(null);
    accountCountRef.current = accounts.length;
    // Open the window synchronously inside the click gesture — browsers
    // block window.open after an await, which makes the button appear dead.
    const popup = window.open('', 'social-connect', 'width=600,height=760');
    if (popup) {
      try {
        popup.document.write(
          '<title>Connecting…</title><body style="font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#444">Connecting…</body>'
        );
      } catch {
        // Cross-origin reuse of the named window — ignore, we redirect below.
      }
    }
    try {
      const { url } = await createSocialConnectUrl(platform);
      if (!url) throw new Error('No connect URL returned');
      if (popup && !popup.closed) {
        popup.location.href = url;
        popupRef.current = popup;
        startPolling(platform);
      } else {
        // Popup was blocked outright — use the current tab as a fallback.
        window.location.assign(url);
      }
    } catch (err) {
      if (popup && !popup.closed) popup.close();
      setError(`Failed to start ${platform} connection`);
    } finally {
      setConnectingPlatform(null);
    }
  }, [accounts.length, startPolling]);

  const handleRemoveConfirmed = useCallback(async () => {
    const account = accountToRemove;
    if (!account) return;
    setAccountToRemove(null);
    setRemovingId(account.id);
    try {
      await removeSocialAccount(account.id);
      setAccounts((prev) => prev.filter((a) => a.id !== account.id));
    } catch (err) {
      setError('Failed to remove account');
    } finally {
      setRemovingId(null);
    }
  }, [accountToRemove]);

  const pollAdAttempt = useCallback((attemptId, stateToken, platform, popup, startedAt = Date.now()) => {
    if (adPollRef.current) clearTimeout(adPollRef.current);
    adPopupRef.current = popup;
    const poll = async () => {
      try {
        const attempt = await getAdConnectAttempt(attemptId, stateToken);
        if (attempt.status === 'completed') {
          if (attempt.connectionId) await syncAdIntegration(attempt.connectionId);
          await loadAdConnections({ silent: true });
          setAdPending(null);
          adPollRef.current = null;
          adPopupRef.current = null;
          if (popup && !popup.closed) popup.close();
          return;
        }
        if (['failed', 'expired'].includes(attempt.status) || Date.now() - startedAt > POLL_MAX_MS) {
          setError(`The ${platform} ads connection did not complete.`);
          setAdPending(null);
          adPollRef.current = null;
          return;
        }
        if (popup?.closed) {
          setError(`The ${platform} ads connection window was closed before setup completed.`);
          setAdPending(null);
          adPollRef.current = null;
          adPopupRef.current = null;
          return;
        }
      } catch (err) {
        console.error('Ads connection status check failed:', err);
        if (Date.now() - startedAt > POLL_MAX_MS) { setError(`The ${platform} ads connection did not complete.`); setAdPending(null); return; }
      }
      adPollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };
    adPollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
  }, [loadAdConnections]);

  const handleAdConnect = useCallback(async (platform) => {
    setAdConnecting(platform); setError(null);
    if (adPollRef.current) clearTimeout(adPollRef.current);
    if (adPopupRef.current && !adPopupRef.current.closed) adPopupRef.current.close();
    const popup = window.open('', adPopupName(platform), 'width=660,height=800');
    try {
      const result = await beginAdIntegration(platform, { return_url: `${window.location.origin}/integrations?ads=connected`, force: Boolean(adConnections.find((item) => item.platform === platform)) });
      if (result.authUrl) {
        if (popup) popup.location.href = result.authUrl;
        else window.location.href = result.authUrl;
      } else if (popup) popup.close();
      setAdPending(platform);
      pollAdAttempt(result.attemptId, result.stateToken, platform, popup);
    } catch (err) {
      if (popup) popup.close();
      setError(`Could not connect ${platform} ads`);
    } finally { setAdConnecting(null); }
  }, [adConnections, pollAdAttempt]);

  const handleAdSync = useCallback(async (connection) => {
    setAdSyncing(connection.id); setError(null);
    try { await syncAdIntegration(connection.id); await loadAdConnections({ silent: true }); }
    catch (err) { setError('Failed to sync ad accounts'); }
    finally { setAdSyncing(null); }
  }, [loadAdConnections]);

  const handleAdRemove = useCallback(async () => {
    if (!adToRemove) return;
    try { await removeAdIntegration(adToRemove.id); setAdToRemove(null); await loadAdConnections({ silent: true }); }
    catch (err) { setError('Failed to disconnect ads account'); }
  }, [adToRemove, loadAdConnections]);

  const saveAdPolicy = useCallback(async () => {
    if (!adPolicyDraft) return;
    setAdPolicyBusy(true); setError(null);
    try {
      await updateAdPolicy({
        allow_unattended_activation: Boolean(adPolicyDraft.allowUnattendedActivation),
        currency_limits: Object.fromEntries(Object.entries(adPolicyDraft.currencyLimits || {}).filter(([, value]) => Number(value?.maxPlannedSpendMinor) > 0).map(([currency, value]) => [currency, Object.fromEntries(Object.entries(value).filter(([, amount]) => Number(amount) > 0).map(([key, amount]) => [key, Number(amount)]))])),
      });
      await loadAdPolicy();
    } catch (err) { setError('Could not save the ads activation policy.'); }
    finally { setAdPolicyBusy(false); }
  }, [adPolicyDraft, loadAdPolicy]);

  const adCurrencies = useMemo(() => {
    const values = Object.values(adAccounts).flat().map((item) => item.currency).filter(Boolean);
    return [...new Set([...Object.keys(adPolicyDraft?.currencyLimits || {}), ...values, 'USD'])].sort();
  }, [adAccounts, adPolicyDraft?.currencyLimits]);

  const accountsByPlatform = useMemo(() => {
    const grouped = {};
    for (const account of accounts) {
      const key = account.platform || 'unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(account);
    }
    return grouped;
  }, [accounts]);

  const connectedIntegrations = useMemo(() => {
    const connected = [];
    if (slackConnected) {
      connected.push({ id: 'slack-agent', name: 'Latted Agent for Slack', key: 'slack', Icon: SlackMark });
    }
    for (const provider of PROVIDERS) {
      if ((accountsByPlatform[provider.key] || []).some((account) => account.status === 'connected')) {
        connected.push({ id: `social-${provider.key}`, name: provider.name, key: provider.key, Icon: provider.Icon });
      }
    }
    for (const provider of AD_PROVIDERS) {
      if (adConnections.some((connection) => connection.platform === provider.key && connection.status === 'active')) {
        connected.push({ id: `ads-${provider.key}`, name: provider.name, key: provider.key, Icon: provider.Icon });
      }
    }
    return connected;
  }, [accountsByPlatform, adConnections, slackConnected]);

  return (
    <AuthenticatedLayout>
      <div className="integrations-page-content">
        <div className="integrations-page-header">
          <div className="integrations-page-header-left">
            <h1 className="integrations-page-title">Integrations</h1>
            <p className="integrations-page-subtitle">
              Bring your publishing, advertising, and teamwork tools into Latted.
            </p>
          </div>
        </div>

        {error && (
          <div className="integrations-error">
            <AlertCircle size={15} />
            {error}
          </div>
        )}

        {pendingPlatform && (
          <div className="integrations-pending">
            <Loader2 size={15} className="integrations-spin" />
            Finish connecting your {PROVIDERS.find((p) => p.key === pendingPlatform)?.name || pendingPlatform} account
            in the window that just opened — this list updates automatically.
          </div>
        )}

        <section className="integrations-connected" aria-labelledby="connected-integrations-heading">
          <div className="integrations-connected-heading">
            <h2 id="connected-integrations-heading">Connected</h2>
            <span>{connectedIntegrations.length ? `${connectedIntegrations.length} active` : 'Your connected tools will appear here'}</span>
          </div>
          {connectedIntegrations.length > 0 && (
            <div className="integrations-connected-list">
              {connectedIntegrations.map(({ id, name, key, Icon }) => (
                <div className={`integrations-connected-icon integrations-card-logo--${key}`} key={id} title={name} aria-label={name}>
                  <Icon size={20} />
                </div>
              ))}
            </div>
          )}
        </section>

        <SlackAgentIntegrationSection onConnectionChange={setSlackConnected} />

        <>
          <div className="integrations-section-heading">
            <div><h2>Social publishing</h2><p>Publish videos and posts without leaving your workflow.</p></div>
          </div>

          {isLoading && accounts.length === 0 ? (
            <div className="integrations-loading"><Loader2 size={15} className="integrations-spin" /> Loading integrations…</div>
          ) : (
            <div className="integrations-grid">
              {PROVIDERS.map((provider) => (
                <ProviderCard
                  key={provider.key}
                  provider={provider}
                  accounts={accountsByPlatform[provider.key] || []}
                  onConnect={handleConnect}
                  connecting={connectingPlatform === provider.key}
                  onRemove={setAccountToRemove}
                  removingId={removingId}
                />
              ))}
            </div>
          )}
        </>

        {adsAvailable && <><div className="integrations-section-heading">
          <div><h2>Paid advertising</h2><p>Campaign reporting and paused-by-default ad creation for personal or organization workflows.</p></div>
          {adLoading && <Loader2 size={15} className="integrations-spin" />}
        </div>
        {adPending && <div className="integrations-pending"><Loader2 size={15} className="integrations-spin" />Finish connecting {AD_PROVIDERS.find((item) => item.key === adPending)?.name || adPending} in the window that opened.</div>}
        <div className="integrations-grid">
          {AD_PROVIDERS.map((provider) => {
            const connection = adConnections.find((item) => item.platform === provider.key);
            return <AdProviderCard key={provider.key} provider={provider} connection={connection} accounts={connection ? adAccounts[connection.id] || [] : []} onConnect={handleAdConnect} connecting={adConnecting === provider.key} onSync={handleAdSync} syncing={adSyncing === connection?.id} onRemove={setAdToRemove} onError={setError} canManage={canManageAds} />;
          })}
        </div>
        {adPolicy?.featureEnabled && adPolicyDraft && <section className="integrations-ad-policy">
          <div><strong>Unattended ad activation</strong><span>Optional organization-level override. Workflow definitions must also opt in, and every currency remains capped.</span></div>
          <label className="integrations-ad-policy__toggle"><input type="checkbox" checked={Boolean(adPolicyDraft.allowUnattendedActivation)} disabled={!adPolicy.canEdit} onChange={(event) => setAdPolicyDraft((current) => ({ ...current, allowUnattendedActivation: event.target.checked }))} /> Allow approved workflows to activate without a person waiting</label>
          {adPolicyDraft.allowUnattendedActivation && <div className="integrations-ad-policy__limits">{adCurrencies.map((currency) => <div key={currency}><strong>{currency} limits (minor units)</strong>{[['dailyBudgetMinor', 'Daily budget'], ['lifetimeBudgetMinor', 'Lifetime budget'], ['maxPlannedSpendMinor', 'Maximum planned spend']].map(([key, label]) => <label key={key}><span>{label}</span><input type="number" min="1" value={adPolicyDraft.currencyLimits?.[currency]?.[key] || ''} disabled={!adPolicy.canEdit} onChange={(event) => setAdPolicyDraft((current) => ({ ...current, currencyLimits: { ...(current.currencyLimits || {}), [currency]: { ...(current.currencyLimits?.[currency] || {}), [key]: Number(event.target.value) } } }))} /></label>)}</div>)}</div>}
          {adPolicy.canEdit && <button type="button" className="integrations-card-connect" disabled={adPolicyBusy || (adPolicyDraft.allowUnattendedActivation && !Object.values(adPolicyDraft.currencyLimits || {}).some((value) => Number(value?.maxPlannedSpendMinor) > 0))} onClick={saveAdPolicy}>{adPolicyBusy ? <Loader2 size={13} className="integrations-spin" /> : null} Save activation policy</button>}
        </section>}</>}

      </div>

      <ConfirmationModal
        isOpen={Boolean(accountToRemove)}
        title="Remove account?"
        message={
          accountToRemove
            ? `${accountToRemove.username ? `@${accountToRemove.username}` : 'This account'} will be disconnected and Latted will no longer be able to post to it.`
            : ''
        }
        confirmLabel="Remove"
        destructive
        onConfirm={handleRemoveConfirmed}
        onCancel={() => setAccountToRemove(null)}
      />
      <ConfirmationModal isOpen={Boolean(adToRemove)} title="Disconnect ads account?" message="Workflow nodes will no longer be able to read campaigns or manage ads through this connection." confirmLabel="Disconnect" destructive onConfirm={handleAdRemove} onCancel={() => setAdToRemove(null)} />
    </AuthenticatedLayout>
  );
}

export default IntegrationsPage;
